import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, Grid, TextField, CircularProgress, MenuItem } from '@mui/material';
import { getAllModules } from '../../../services/adminServices/ModuleServices';
import { addMakerPlanModule, updateMakerPlanModule } from '../../../services/adminServices/MakerPlanServices';
import { toast } from 'react-toastify';

const INITIAL_FORM_STATE = { moduleName: '', codingHours: '', type: '' };
const INITIAL_ERRORS_STATE = {};

const AddModuleModal = ({ open, onClose, moduleData, selectedPlan, fetchData }) => {
    const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState(INITIAL_ERRORS_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [moduleList, setModuleList] = useState([]);

    useEffect(() => {
        if (moduleData) {
            setFormValues({
                moduleName: moduleData.moduleID || '',
                codingHours: moduleData?.Maker_Plan_Module.module_coding_Hrs || '',
                type: moduleData?.Maker_Plan_Module.coding_type || ''
            });
        } else {
            resetFormState();
        }
    }, [moduleData, open]);


    async function getModules() {
        try {
            const modules = await getAllModules();
            setModuleList(modules?.data?.data);
        } catch (error) {
            toast.error( error?.response?.data.message||error?.message ||error?.error);
            setModuleList([]);
        }
    };
    useEffect(() => {
        getModules();
    }, [])

    const resetFormState = () => {
        setFormValues(INITIAL_FORM_STATE);
        setFormErrors(INITIAL_ERRORS_STATE);
        setIsLoading(false);
    };

    const handleInputChange = ({ target: { name, value } }) => {
        if (name === 'type' || name === 'moduleName') {
            setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
            return;
        }
        const numericValue = value.replace(/[^0-9.]/g, '');

        if (numericValue !== value) {
            setFormErrors({ ...formErrors, [name]: 'Please enter valid numbers only' });
        } else {
            setFormErrors({ ...formErrors, [name]: '' });
        }
        setFormValues((prevValues) => ({ ...prevValues, [name]: numericValue }));
    };

    const validateFields = () => {
        const errors = {};
        const { moduleName, codingHours, type } = formValues;

        if (!moduleName) errors.moduleName = 'Module Name is required*';
        if (!codingHours) errors.codingHours = 'Coding Hours are required*';
        if (!type) errors.type = 'Type is required*';

        return errors;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateFields();
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        const obj = {
            module_coding_Hrs: parseInt(formValues.codingHours),
            coding_type: formValues.type
        };

        setIsLoading(true);
        try {
            if (moduleData) {
                const response= await updateMakerPlanModule(
                    moduleData.Maker_Plan_Module.id,
                    selectedPlan,
                    formValues.moduleName,
                    obj
                );
                toast.success(response.data.message);
            } else {
               const response= await addMakerPlanModule(selectedPlan, formValues.moduleName, obj);
                toast.success(response.data.message);
            }
            fetchData(selectedPlan);
            resetFormState();
            onClose();
        } catch (error) {
            toast.error( error?.response?.data.message||error?.message ||error?.error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '45%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {moduleData ? 'Edit Module' : 'Add Module'}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {[{ label: 'Module Name *', name: 'moduleName', type: 'select', options: moduleList, error: formErrors.moduleName },
                        { label: 'Coding Hours *', name: 'codingHours', type: 'text', error: formErrors.codingHours, inputProps: { min: 0, max: 300, pattern: '[0-9]*' } },
                        { label: 'Type *', name: 'type', type: 'select', options: ['Test', 'Practice'], error: formErrors.type },
                        ].map(({ label, name, type, options, error, ...props }) => (
                            <Grid item xs={12} key={name}>
                                {type === 'select' ? (
                                    <TextField
                                        fullWidth
                                        select
                                        label={label}
                                        name={name}
                                        value={formValues[name]}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                        size="small"
                                    >

                                        {name === 'moduleName'
                                            ? options.map((option, index) => (
                                                <MenuItem key={index} value={option.moduleID}>
                                                    {option.module_Name}
                                                </MenuItem>
                                            ))
                                            : options.map((option, index) => (
                                                <MenuItem key={index} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type={type}
                                        label={label}
                                        name={name}
                                        value={formValues[name]}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                        {...props}
                                    />
                                )}
                            </Grid>
                        ))}
                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                sx={{ marginRight: 2 }}
                            >
                                {isLoading ? <CircularProgress size={24} /> : (moduleData ? 'Save' : 'Add')}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Modal>
    );
};

export default AddModuleModal;
